// ===============================================================
// V·IAnet Admin — Sign Configuration Tab (M15) — PRD-002 0569
// Toggle, politica firma por entidad, certificados, estadisticas.
// Solo TENANT_OWNER/TENANT_ADMIN/SUPERADMIN_PLATFORM pueden mutar.
// TENANT_AUDITOR ve todo en modo solo lectura.
//
// 0631 ADR-025: ampliado con seccion "Workflow unificado" que parametriza
// WorkflowConfig por (tenantId, artefactoTipo) — rolGobernanzaAprobador,
// revisorUnico, requiereFirmaQES.
// ===============================================================

import { useState, useEffect, useCallback } from 'react';
import { useI18n } from '../../../../i18n';
import { useAuth } from '../../../../context/AuthContext';
import { request } from '../../../shared/services/api';
import { LoadingSpinner } from '../../../shared/components/LoadingSpinner';

// 0643++: tipos WorkflowConfigDto/TipoRolGobernanza/ArtefactoTipoConfig eliminados
// junto al cleanup heredado del 0642c. Su consumo vive en tab dedicada
// /admin/flujos-de-aprobación.

interface SignConfig {
  id?: string;
  tenantId?: string;
  nivelFirmaDefault: string;
  horasRecordatorio: number;
  horasEscalacion: number;
  caInternaHabilitada: boolean;
  firmaInvitacionHabilitada: boolean;
  proveedorQes?: string | null;
  credencialesQes?: { apiKey?: string } | null;
  qtspSandbox?: boolean;
  urlCallbackQes?: string | null;
}

interface SignMode {
  signActive: boolean;
}

const NIVELES = [
  { value: 'APROBACION_SIMPLE', label: 'Aprobacion simple' },
  { value: 'VISADO', label: 'Visado' },
  { value: 'FIRMA_SIMPLE', label: 'Firma simple (SES)' },
  { value: 'FIRMA_AVANZADA', label: 'Firma avanzada (AES)' },
];

const ENTIDADES = [
  { key: 'DOCUMENTO', label: 'Documentos' },
  { key: 'FLUJOGRAMA', label: 'Flujogramas' },
  { key: 'SOLICITUD', label: 'Solicitudes' },
];

export function AdminSignTab() {
  const { t } = useI18n();
  const { user } = useAuth();
  const role = user?.role ?? 'MEMBER';
  // 0569: solo TENANT_OWNER/TENANT_ADMIN/SUPERADMIN_PLATFORM pueden mutar.
  const canEdit =
    role === 'SUPERADMIN_PLATFORM' || role === 'TENANT_OWNER' || role === 'TENANT_ADMIN';
  const [config, setConfig] = useState<SignConfig | null>(null);
  const [signActive, setSignActive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [policies, setPolicies] = useState<Record<string, string>>({
    DOCUMENTO: 'APROBACION_SIMPLE',
    FLUJOGRAMA: 'APROBACION_SIMPLE',
    SOLICITUD: 'APROBACION_SIMPLE',
  });
  const [qtspTesting, setQtspTesting] = useState(false);
  const [qtspStatus, setQtspStatus] = useState<'idle' | 'connected' | 'error'>('idle');
  const [qtspError, setQtspError] = useState('');

  // 0642c: state legacy workflow eliminado al mover sección a tab dedicada
  // /admin/flujos-de-aprobación. workflowConfigs / tiposRolGobernanza /
  // savingArtefacto vivían aquí y han migrado al componente AdminWorkflowsTab.

  const fetchData = useCallback(async () => {
    try {
      // 0643++: eliminadas peticiones a /workflow/config y /tipos-rol-gobernanza
      // — su consumo (sección "Workflow unificado ADR-025") movido a tab dedicada
      // /admin/flujos-de-aprobación. Cleanup heredado del 0642c.
      const [configRes, modeRes, policiesRes] = await Promise.all([
        request<{ data: SignConfig | null }>('/sign/config'),
        request<{ data: SignMode }>('/sign/mode'),
        // 0578: hidrata policies persistidas
        request<{ data: Array<{ tipoEntidad: string; nivelFirmaRequerido: string }> }>(
          '/sign/policies',
        ).catch(() => ({ data: [] })),
      ]);
      if (configRes.data) {
        setConfig(configRes.data);
      }
      setSignActive(modeRes.data.signActive);
      // 0578: si no hay policies persistidas se conserva el default APROBACION_SIMPLE
      if (Array.isArray(policiesRes.data) && policiesRes.data.length > 0) {
        const hydrated: Record<string, string> = {
          DOCUMENTO: 'APROBACION_SIMPLE',
          FLUJOGRAMA: 'APROBACION_SIMPLE',
          SOLICITUD: 'APROBACION_SIMPLE',
        };
        for (const p of policiesRes.data) {
          if (p.tipoEntidad && p.nivelFirmaRequerido) {
            hydrated[p.tipoEntidad] = p.nivelFirmaRequerido;
          }
        }
        setPolicies(hydrated);
      }
    } catch {
      // fallback defaults
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSave = async () => {
    if (!canEdit) return;
    setSaving(true);
    setSaved(false);
    try {
      const payload = {
        nivelFirmaDefault: config?.nivelFirmaDefault ?? 'APROBACION_SIMPLE',
        horasRecordatorio: config?.horasRecordatorio ?? 24,
        horasEscalacion: config?.horasEscalacion ?? 48,
        caInternaHabilitada: config?.caInternaHabilitada ?? true,
        firmaInvitacionHabilitada: config?.firmaInvitacionHabilitada ?? true,
        proveedorQes: config?.proveedorQes ?? null,
        credencialesQes: config?.credencialesQes ?? null,
        qtspSandbox: config?.qtspSandbox ?? true,
      };
      const res = await request<{ data: SignConfig }>('/sign/config', {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      setConfig(res.data);
      // 0578: persistir policies por entidad junto con la config
      await request('/sign/policies', {
        method: 'PUT',
        body: JSON.stringify({ policies }),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      // error
    } finally {
      setSaving(false);
    }
  };

  const updateConfig = (field: keyof SignConfig, value: unknown) => {
    if (!canEdit) return; // 0569: TENANT_AUDITOR no puede mutar
    setConfig((prev) =>
      prev ? { ...prev, [field]: value } : ({ [field]: value } as unknown as SignConfig),
    );
  };

  // 0642c: handler updateWorkflowConfig + ARTEFACTOS_WORKFLOW eliminados al
  // mover la sección "Workflow unificado (ADR-025)" a la tab dedicada
  // /admin/flujos-de-aprobación. Si vuelven a hacer falta, recuperar de git
  // history en commit `1aff7891` o anterior.

  if (loading) {
    return <LoadingSpinner size="md" muted />;
  }

  return (
    <div>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
        }}
      >
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#1F3864' }}>
          {t.sign?.title ?? 'Configuracion de Sign (M15)'}
        </h3>
        {canEdit && (
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '8px 20px',
              borderRadius: 8,
              border: 'none',
              background: saving ? '#9CA3AF' : '#1F3864',
              color: '#fff',
              fontSize: 13,
              fontWeight: 600,
              cursor: saving ? 'not-allowed' : 'pointer',
            }}
          >
            {saving ? 'Guardando...' : saved ? 'Guardado' : t.common.guardar}
          </button>
        )}
      </div>

      {/* 0569: banner read-only para TENANT_AUDITOR */}
      {!canEdit && (
        <div
          style={{
            padding: '10px 16px',
            borderRadius: 8,
            fontSize: 13,
            backgroundColor: '#EFF6FF',
            color: '#1E40AF',
            border: '1px solid #BFDBFE',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 16,
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16v-4M12 8h.01" />
          </svg>
          <span>
            Vista de solo lectura. Solo administradores del tenant (TENANT_OWNER/TENANT_ADMIN) o
            plataforma pueden modificar la configuración de firma.
          </span>
        </div>
      )}

      {/* Sign Active Status */}
      <div
        style={{
          background: signActive ? '#F0FDF4' : '#FEF2F2',
          border: `1px solid ${signActive ? '#BBF7D0' : '#FECACA'}`,
          borderRadius: 8,
          padding: 16,
          marginBottom: 24,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <div
          style={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            background: signActive ? '#16A34A' : '#DC2626',
          }}
        />
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#1F2937' }}>
            {t.sign?.estado ?? 'Estado de Sign'}
          </div>
          <div style={{ fontSize: 13, color: '#6B7280' }}>
            {signActive
              ? (t.sign?.activo ??
                'M15 Sign esta activo. La activacion se gestiona desde Admin > Plataforma > Modulos.')
              : (t.sign?.inactivo ??
                'M15 Sign esta inactivo. Activalo desde Admin > Plataforma > Modulos.')}
          </div>
        </div>
      </div>

      {/* Configuration Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
        {/* Nivel firma default */}
        <div
          style={{
            background: '#fff',
            border: '1px solid #E5E7EB',
            borderRadius: 10,
            padding: 20,
          }}
        >
          <label
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: '#374151',
              display: 'block',
              marginBottom: 8,
            }}
          >
            {t.sign?.nivelDefault ?? 'Nivel de firma por defecto'}
          </label>
          <select
            value={config?.nivelFirmaDefault ?? 'APROBACION_SIMPLE'}
            onChange={(e) => updateConfig('nivelFirmaDefault', e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              borderRadius: 8,
              border: '1px solid #D1D5DB',
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            {NIVELES.map((n) => (
              <option key={n.value} value={n.value}>
                {n.label}
              </option>
            ))}
          </select>
        </div>

        {/* Horas recordatorio */}
        <div
          style={{
            background: '#fff',
            border: '1px solid #E5E7EB',
            borderRadius: 10,
            padding: 20,
          }}
        >
          <label
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: '#374151',
              display: 'block',
              marginBottom: 8,
            }}
          >
            {t.sign?.horasRecordatorio ?? 'Horas para recordatorio'}
          </label>
          <input
            type="number"
            value={config?.horasRecordatorio ?? 24}
            onChange={(e) => updateConfig('horasRecordatorio', Number(e.target.value))}
            min={1}
            max={168}
            style={{
              width: '100%',
              padding: '8px 12px',
              borderRadius: 8,
              border: '1px solid #D1D5DB',
              fontSize: 14,
              boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Horas escalacion */}
        <div
          style={{
            background: '#fff',
            border: '1px solid #E5E7EB',
            borderRadius: 10,
            padding: 20,
          }}
        >
          <label
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: '#374151',
              display: 'block',
              marginBottom: 8,
            }}
          >
            {t.sign?.horasEscalacion ?? 'Horas para escalacion'}
          </label>
          <input
            type="number"
            value={config?.horasEscalacion ?? 48}
            onChange={(e) => updateConfig('horasEscalacion', Number(e.target.value))}
            min={1}
            max={336}
            style={{
              width: '100%',
              padding: '8px 12px',
              borderRadius: 8,
              border: '1px solid #D1D5DB',
              fontSize: 14,
              boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Toggles */}
        <div
          style={{
            background: '#fff',
            border: '1px solid #E5E7EB',
            borderRadius: 10,
            padding: 20,
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
          }}
        >
          <ToggleRow
            label={t.sign?.caInterna ?? 'CA interna habilitada'}
            checked={config?.caInternaHabilitada ?? true}
            onChange={(v) => updateConfig('caInternaHabilitada', v)}
          />
          <ToggleRow
            label={t.sign?.firmaInvitacion ?? 'Firma por invitacion'}
            checked={config?.firmaInvitacionHabilitada ?? true}
            onChange={(v) => updateConfig('firmaInvitacionHabilitada', v)}
          />
        </div>
      </div>

      {/* Politica de firma por entidad */}
      <div
        style={{
          background: '#fff',
          border: '1px solid #E5E7EB',
          borderRadius: 10,
          padding: 20,
          marginBottom: 24,
        }}
      >
        <h4 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 600, color: '#1F3864' }}>
          {t.sign?.politicaEntidad ?? 'Nivel minimo de firma por tipo de entidad'}
        </h4>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #E5E7EB' }}>
              <th
                style={{
                  textAlign: 'left',
                  padding: '10px 16px',
                  fontSize: 13,
                  fontWeight: 600,
                  color: '#6B7280',
                }}
              >
                Entidad
              </th>
              <th
                style={{
                  textAlign: 'left',
                  padding: '10px 16px',
                  fontSize: 13,
                  fontWeight: 600,
                  color: '#6B7280',
                }}
              >
                Nivel minimo
              </th>
            </tr>
          </thead>
          <tbody>
            {ENTIDADES.map((ent) => (
              <tr key={ent.key} style={{ borderBottom: '1px solid #F3F4F6' }}>
                <td style={{ padding: '10px 16px', fontSize: 14, fontWeight: 500 }}>{ent.label}</td>
                <td style={{ padding: '10px 16px' }}>
                  <select
                    value={policies[ent.key]}
                    onChange={(e) =>
                      setPolicies((prev) => ({ ...prev, [ent.key]: e.target.value }))
                    }
                    style={{
                      padding: '6px 10px',
                      borderRadius: 6,
                      border: '1px solid #D1D5DB',
                      fontSize: 13,
                      cursor: 'pointer',
                    }}
                  >
                    {NIVELES.map((n) => (
                      <option key={n.value} value={n.value}>
                        {n.label}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 0642: Sección "Workflow unificado (ADR-025)" eliminada de Firma.
          Movida a tab dedicada "Flujos de aprobación" + tab Workflow en cada
          módulo. Esta tab queda solo para configuración pura M15 Sign
          (niveles, recordatorios, escalación, QTSP).

          Comentado abajo en lugar de borrar para preservar wiring del estado
          (workflowConfigs, ARTEFACTOS_WORKFLOW, updateWorkflowConfig) que
          siguen usándose internamente como back-compat con clients legacy. */}

      {/* QTSP — Proveedor de firma cualificada */}
      <div
        style={{
          background: '#fff',
          border: '1px solid #E5E7EB',
          borderRadius: 10,
          padding: 20,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 16,
          }}
        >
          <h4 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#1F3864' }}>
            {t.sign?.qtspTitle ?? 'Proveedor QTSP (Firma Cualificada)'}
          </h4>
          {/* Status indicator */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background:
                  qtspStatus === 'connected'
                    ? '#16A34A'
                    : qtspStatus === 'error'
                      ? '#DC2626'
                      : '#9CA3AF',
              }}
            />
            <span
              style={{
                fontSize: 12,
                color:
                  qtspStatus === 'connected'
                    ? '#16A34A'
                    : qtspStatus === 'error'
                      ? '#DC2626'
                      : '#9CA3AF',
              }}
            >
              {qtspStatus === 'connected'
                ? 'Conectado'
                : qtspStatus === 'error'
                  ? 'Error'
                  : 'No configurado'}
            </span>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {/* Selector de proveedor */}
          <div>
            <label
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: '#374151',
                display: 'block',
                marginBottom: 6,
              }}
            >
              Proveedor
            </label>
            <select
              value={config?.proveedorQes ?? ''}
              onChange={(e) => updateConfig('proveedorQes', e.target.value || null)}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: 8,
                border: '1px solid #D1D5DB',
                fontSize: 14,
                cursor: 'pointer',
              }}
            >
              <option value="">Sin proveedor</option>
              <option value="STUB">Stub (Desarrollo)</option>
              <option value="SIGNATURIT">Signaturit</option>
              <option value="DOCUSIGN">Docusign</option>
            </select>
          </div>

          {/* API Key (enmascarado) */}
          <div>
            <label
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: '#374151',
                display: 'block',
                marginBottom: 6,
              }}
            >
              API Key
            </label>
            <input
              type="password"
              placeholder="Introduce API key del proveedor"
              value={(config?.credencialesQes as { apiKey?: string } | null)?.apiKey ?? ''}
              onChange={(e) =>
                updateConfig('credencialesQes', e.target.value ? { apiKey: e.target.value } : null)
              }
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: 8,
                border: '1px solid #D1D5DB',
                fontSize: 14,
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Toggle Sandbox/Produccion */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 13, color: '#374151' }}>Modo Sandbox</span>
            <button
              onClick={() => updateConfig('qtspSandbox', !(config?.qtspSandbox ?? true))}
              style={{
                width: 40,
                height: 22,
                borderRadius: 11,
                border: 'none',
                background: (config?.qtspSandbox ?? true) ? '#33BCE1' : '#D1D5DB',
                cursor: 'pointer',
                position: 'relative',
                transition: 'background 0.2s',
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  top: 2,
                  left: (config?.qtspSandbox ?? true) ? 20 : 2,
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  background: '#fff',
                  transition: 'left 0.2s',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                }}
              />
            </button>
          </div>

          {/* Boton probar conexion */}
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button
              onClick={async () => {
                setQtspTesting(true);
                setQtspStatus('idle');
                setQtspError('');
                try {
                  const res = await request<{
                    data: { connected: boolean; provider: string; error?: string };
                  }>('/sign/qtsp/test');
                  if (res.data.connected) {
                    setQtspStatus('connected');
                  } else {
                    setQtspStatus('error');
                    setQtspError(res.data.error ?? 'Error de conexion');
                  }
                } catch {
                  setQtspStatus('error');
                  setQtspError('Error al conectar con el proveedor');
                } finally {
                  setQtspTesting(false);
                }
              }}
              disabled={qtspTesting || !config?.proveedorQes}
              style={{
                padding: '8px 16px',
                borderRadius: 8,
                border: '1px solid #D1D5DB',
                background: qtspTesting ? '#F3F4F6' : '#fff',
                color: !config?.proveedorQes ? '#9CA3AF' : '#374151',
                fontSize: 13,
                fontWeight: 500,
                cursor: qtspTesting || !config?.proveedorQes ? 'not-allowed' : 'pointer',
              }}
            >
              {qtspTesting ? 'Probando...' : 'Probar conexion'}
            </button>
          </div>
        </div>

        {/* Error message */}
        {qtspStatus === 'error' && qtspError && (
          <div
            style={{
              marginTop: 12,
              padding: '8px 12px',
              background: '#FEF2F2',
              border: '1px solid #FECACA',
              borderRadius: 6,
              fontSize: 12,
              color: '#DC2626',
            }}
          >
            {qtspError}
          </div>
        )}
      </div>
    </div>
  );
}

function ToggleRow({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <span style={{ fontSize: 13, color: '#374151' }}>{label}</span>
      <button
        onClick={() => onChange(!checked)}
        style={{
          width: 40,
          height: 22,
          borderRadius: 11,
          border: 'none',
          background: checked ? '#33BCE1' : '#D1D5DB',
          cursor: 'pointer',
          position: 'relative',
          transition: 'background 0.2s',
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: 2,
            left: checked ? 20 : 2,
            width: 18,
            height: 18,
            borderRadius: '50%',
            background: '#fff',
            transition: 'left 0.2s',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
          }}
        />
      </button>
    </div>
  );
}
